import { useEffect, useState } from 'react';

import { HeaderContent, HEADER_TITLE_CLASS } from '../../components/shell/app-header';
import { api } from '../../lib/api';
import { avecContexteNav } from '../../lib/mini-app-contexte';
import { memoriserPrenomPassation } from './fluence-prenom-passation';
import { NIVEAUX_FLUENCE as NIVEAUX, enDecrochage, estExtrapolee, memeNiveau, niveauCouvert, pastilleDe } from './fluence-reperes';

type Niveau = (typeof NIVEAUX)[number];

interface Classe { id: string; name: string; level_code?: string }
interface Eleve { id: string; first_name: string; last_name: string; active: boolean }
interface Support { id: string; ref: string; titre: string; genre: string | null; auteur: string | null; nombreMots: number; motsComptes: number; mclmExclu: boolean }
interface EnAttente {
  evaluationId: string; studentId: string; prenom: string; nom: string;
  supportId: string; supportTitre: string; levelCode: string; assignedAt: string;
}
interface Point {
  evaluationId: string; studentId: string; completedAt: string; supportTitre: string; mclm: number; dureeSecondes: number;
  seuilMin: number | null; seuilMax: number | null; position: 'sous' | 'dans' | 'au-dessus' | null;
  comprehensionReussies: number | null; comprehensionTotal: number | null;
}

/* Le fil d'ariane et le retour vers « Suivi de classe » : cet écran est
   toujours ouvert AVEC une classe déjà choisie, jamais isolément — retenir
   son id dans l'URL est ce qui permet à « Évaluer » et au clic sur un élève
   de fabriquer un chemin de retour qui la restaure. */
const lienPassation = (evaluationId: string, supportId: string, prenom: string, classeNom: string, classeId: string) => {
  memoriserPrenomPassation(evaluationId, prenom);
  return avecContexteNav(
    `/mini-apps/fluence-eleve?evaluation=${evaluationId}&support=${supportId}`,
    { contexte: classeNom, retourHref: `/mini-apps/fluence-enseignant?classe=${classeId}`, retourLabel: 'Suivi de classe' },
  );
};

export default function FluenceEnseignant() {
  const classeIdInitiale = new URLSearchParams(window.location.search).get('classe') ?? '';
  const [classes, setClasses] = useState<Classe[]>([]);
  const [etatClasses, setEtatClasses] = useState<'chargement' | 'chargees' | 'erreur'>('chargement');
  const [tentativeClasses, setTentativeClasses] = useState(0);
  const [classeId, setClasseId] = useState(classeIdInitiale);
  const [eleves, setEleves] = useState<Eleve[]>([]);
  const [etatEleves, setEtatEleves] = useState<'chargement' | 'charge' | 'erreur'>('chargement');
  const [tentativeEleves, setTentativeEleves] = useState(0);
  const [niveau, setNiveau] = useState<Niveau>('CE1');
  const [supports, setSupports] = useState<Support[]>([]);
  const [chargementSupports, setChargementSupports] = useState(true);
  const [supportId, setSupportId] = useState('');
  const [progression, setProgression] = useState<Point[]>([]);
  const [enAttente, setEnAttente] = useState<EnAttente[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');


  useEffect(() => {
    const classeDepuisUrl = new URLSearchParams(window.location.search).get('classe');
    let obsolete = false;
    setEtatClasses('chargement');
    setMessage(null);
    void api.teacher.myClassrooms.query()
      .then((rows) => {
        if (obsolete) return;
        const cs = rows as Classe[];
        setClasses(cs);
        setEtatClasses('chargees');
        if (!classeDepuisUrl && cs[0]) setClasseId(cs[0].id);
      })
      .catch(() => {
        if (obsolete) return;
        setMessage("Impossible de charger vos classes.");
        setEtatClasses('erreur');
      });
    return () => { obsolete = true; };
  }, [tentativeClasses]);

  useEffect(() => {
    if (!classeId) return;
    let obsolete = false;
    setEtatEleves('chargement');
    setAjoutOuvert(false);
    setPrenom('');
    setNom('');
    void api.teacher.classroomStudents.query({ classroomId: classeId })
      /* `teacher.classroomStudents` rend AUSSI les élèves désactivés — un enfant
         parti de l'établissement. Or `assigner` ne retient que les inscrits
         actifs : proposer « Évaluer » sur un nom que l'API refusera produisait
         un échec opaque, « L'évaluation n'a pas pu être ouverte », sans dire
         pourquoi. On ne montre donc que la classe telle qu'elle est aujourd'hui.
         Le dépôt partagé n'est pas touché : d'autres écrans en dépendent. */
      .then((rows) => {
        if (obsolete) return;
        setEleves((rows as Eleve[]).filter((e) => e.active));
        setEtatEleves('charge');
      })
      .catch(() => {
        if (obsolete) return;
        setEleves([]);
        setEtatEleves('erreur');
      });
    return () => { obsolete = true; };
  }, [classeId, tentativeEleves]);

  useEffect(() => {
    setChargementSupports(true);
    void (api as never as { miniApps: { fluence: { supports: { list: { query: (i: unknown) => Promise<Support[]> } } } } })
      .miniApps.fluence.supports.list.query({ levelCode: niveau })
      .then((rows) => { setSupports(rows); setSupportId(rows[0]?.id ?? ''); })
      .catch(() => setSupports([]))
      .finally(() => setChargementSupports(false));
  }, [niveau]);

  const chargerEnAttente = () => {
    if (!classeId) return;
    void (api as never as { miniApps: { fluence: { evaluations: { enAttente: { query: (i: unknown) => Promise<EnAttente[]> } } } } })
      .miniApps.fluence.evaluations.enAttente.query({ classroomId: classeId })
      .then(setEnAttente)
      .catch(() => setEnAttente([]));
  };
  useEffect(chargerEnAttente, [classeId]);

  const chargerProgression = () => {
    if (!classeId) return;
    void (api as never as { miniApps: { fluence: { progression: { classe: { query: (i: unknown) => Promise<Point[]> } } } } })
      .miniApps.fluence.progression.classe.query({ classroomId: classeId })
      .then(setProgression)
      .catch(() => setProgression([]));
  };
  useEffect(chargerProgression, [classeId]);

  /* L'EXCEPTION ASSUMÉE. Inscrire une classe se fait côté direction ; ce bouton
     répare l'oubli qu'on découvre AVEC L'ENFANT DEVANT SOI, au moment de le
     faire lire. Sortir de l'application pour obtenir une inscription, c'est
     perdre la séance. D'où un élève à la fois, et pas un import. */
  const ajouterEleve = async () => {
    if (!classeId || prenom.trim() === '' || nom.trim() === '') {
      setMessage('Un élève a besoin d’un prénom et d’un nom.');
      return;
    }
    setEnCours(true); setMessage(null);
    try {
      await (api as never as { miniApps: { fluence: { eleves: { ajouter: { mutate: (i: unknown) => Promise<unknown> } } } } })
        .miniApps.fluence.eleves.ajouter.mutate({ classroomId: classeId, prenom: prenom.trim(), nom: nom.trim() });
      /* On RELIT la classe plutôt que d'ajouter la ligne à la main : l'écran
         montre alors ce que la base contient, et non ce qu'on croit y avoir mis. */
      const rows = await api.teacher.classroomStudents.query({ classroomId: classeId });
      setEleves((rows as Eleve[]).filter((e) => e.active));
      setPrenom(''); setNom(''); setAjoutOuvert(false);
    } catch {
      setMessage("L’élève n’a pas pu être ajouté.");
    } finally {
      setEnCours(false);
    }
  };

  /* Le geste du quotidien : un bouton par élève. L'enseignant appelle un enfant,
     touche « Évaluer », et l'écran de lecture s'ouvre déjà réglé sur lui et sur
     le texte choisi. L'assignation n'est plus une étape à part — elle se fait
     au moment où on prend l'enfant. */
  const evaluer = async (el: Eleve) => {
    if (!classeId || !supportId) { setMessage("Choisissez d'abord un texte."); return; }
    const classeNom = classes.find((c) => c.id === classeId)?.name ?? '';
    const dejaLa = enAttente.find((e) => e.studentId === el.id && e.supportId === supportId);
    if (dejaLa) {
      window.location.href = lienPassation(dejaLa.evaluationId, supportId, el.first_name, classeNom, classeId);
      return;
    }
    setEnCours(true); setMessage(null);
    try {
      const r = await (api as never as { miniApps: { fluence: { evaluations: { assigner: { mutate: (i: unknown) => Promise<{ id: string }[]> } } } } })
        .miniApps.fluence.evaluations.assigner.mutate({ classroomId: classeId, supportId, studentIds: [el.id] });
      const cree = r[0];
      if (!cree) { setMessage("L'évaluation n'a pas pu être ouverte."); return; }
      window.location.href = lienPassation(cree.id, supportId, el.first_name, classeNom, classeId);
    } catch {
      setMessage("L'évaluation n'a pas pu être ouverte.");
    } finally {
      setEnCours(false);
    }
  };

  /* Le niveau de la classe choisie, rapporte a ce que fluence sait mesurer. */
  const classe = classes.find((c) => c.id === classeId);
  const horsPerimetre = classe !== undefined && !niveauCouvert(classe.level_code);
  const texteAutreNiveau = classe !== undefined && !horsPerimetre
    && supportId !== '' && !memeNiveau(classe.level_code, niveau);

  const carte = 'bg-card border border-border rounded-card shadow-card p-5';
  const champ = 'h-11 min-h-[44px] w-full rounded-sm border border-border bg-card px-3 text-sm text-foreground';

  return (
    <>
      <HeaderContent width="lg" showBurger backTo="/mini-apps" backLabel="Mini-apps">
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Fluence — suivi de classe</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Choisissez un texte, puis « Évaluer » en face d&apos;un élève : la lecture s&apos;ouvre réglée sur lui.
          </p>
        </div>
      </HeaderContent>

      <main className="max-w-[1000px] mx-auto px-5 pb-16 pt-4 flex flex-col gap-5">
        {message ? (
          <p className="m-0 rounded-sm bg-naia-info-bg px-4 py-3 text-sm text-foreground">{message}</p>
        ) : null}

        {/* ON PRÉVIENT, ON N'INTERDIT PAS. Un texte de CM2 donné à un élève de 6e
            en difficulté est une pratique de remédiation ordinaire, et refuser
            une mesure fait perdre une séance. Mais l'écran se taisait
            complètement : le niveau vient du TEXTE, jamais de l'élève, et rien
            ne signalait qu'on mesurait un collégien sur une échelle de primaire. */}
        {horsPerimetre ? (
          <p className="m-0 rounded-sm border border-naia-orange bg-naia-orange-bg px-4 py-3 text-sm text-foreground">
            <b>{classe!.name} est une classe de {classe!.level_code?.toUpperCase()}.</b> La fluence
            se mesure du CP au CM2 : les repères affichés ici sont ceux du niveau du <b>texte</b>,
            pas de la classe. La mesure reste valable, sa comparaison au repère ne l&apos;est pas.
          </p>
        ) : texteAutreNiveau ? (
          <p className="m-0 rounded-sm bg-naia-info-bg px-4 py-3 text-sm text-foreground">
            Texte de <b>{niveau}</b> pour une classe de <b>{classe!.level_code?.toUpperCase()}</b> —
            volontaire pour une remédiation, à vérifier sinon. Le repère appliqué sera celui du texte.
          </p>
        ) : null}

        <section className={carte}>
          <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-3">Le texte du jour</h2>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_1fr] gap-3 items-end">
            <label className="text-[13px] font-bold text-muted-foreground">
              Classe
              <select className={champ} value={classeId} onChange={(e) => setClasseId(e.target.value)} disabled={classes.length === 0} aria-busy={etatClasses === 'chargement'}>
                {/* Un sélecteur vide est un contrôle muet : il ne dit pas s'il
                    attend un chargement, s'il a échoué, ou s'il n'y a rien. */}
                {classes.length === 0
                  ? <option value="">{etatClasses === 'chargement' ? 'Chargement…' : etatClasses === 'erreur' ? 'Classes indisponibles' : 'Aucune classe'}</option>
                  : classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="text-[13px] font-bold text-muted-foreground">
              Niveau
              <select className={champ} value={niveau} onChange={(e) => setNiveau(e.target.value as Niveau)}>
                {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="text-[13px] font-bold text-muted-foreground">
              Texte
              <select className={champ} value={supportId} onChange={(e) => setSupportId(e.target.value)} disabled={supports.length === 0}>
                {supports.length === 0
                  ? <option value="">{chargementSupports ? 'Chargement…' : 'Aucun texte à ce niveau'}</option>
                  : supports.map((s) => (
                    <option key={s.id} value={s.id}>
                      {/* L'auteur au moment du CHOIX : l'enseignant doit savoir
                          qu'il propose une fable de La Fontaine plutôt qu'un récit
                          écrit pour l'occasion. */}
                      {s.titre}{s.auteur ? `, ${s.auteur}` : ''} — {s.motsComptes} mots{s.mclmExclu ? ' · sans mesure de vitesse' : ''}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          {/* Un poème ou une fable se lit en vers, ou se récite en partie de
              mémoire : le nombre de mots par minute n'y veut rien dire. Le dire
              AVANT le choix, pas après la lecture. */}
          {supports.find((s) => s.id === supportId)?.mclmExclu ? (
            <p className="m-0 mt-3 text-[13px] text-muted-foreground">
              Ce texte se lit en vers ou se récite : la lecture sera enregistrée, mais aucune
              vitesse ne lui sera comparée à un repère.
            </p>
          ) : null}
          {/* Le CP n'a pas de repère officiel : le dire ici évite qu'un enseignant
              cherche un chiffre qui n'existe pas. */}
          {niveau === 'CP' ? (
            <p className="m-0 mt-3 text-[13px] text-muted-foreground">
              Au CP, aucun repère chiffré n'est publié : la lecture s'enregistre normalement, mais
              elle se compare à la lecture précédente du même enfant plutôt qu'à une bande.
            </p>
          ) : null}
        </section>

        <section className={carte}>
          <h2 className="text-[15px] font-extrabold text-foreground mt-0 mb-1">Progression de la classe</h2>
          <p className="m-0 mb-3 text-[13px] text-muted-foreground">
            Une lecture, pas un classement — les élèves sont dans l&apos;ordre de la classe.
            « Évaluer » ouvre la lecture avec l&apos;élève, sur le texte choisi ci-dessus.
            {/* LE CHEMIN VERS LA PAGE D'UN ÉLÈVE N'ÉTAIT ÉCRIT NULLE PART. Elle
                n'est pas au catalogue — une carte qui ouvrirait une page vide est
                interdite par la charte, et cet écran n'a aucun sens sans élève
                désigné. Le nom EST un lien depuis le début ; rien ne le disait,
                et une fonctionnalité qu'on ne sait pas atteindre n'existe pas. */}
            {' '}<b className="text-foreground">Toucher un nom</b> ouvre sa progression : courbe,
            compétences et positionnement.
          </p>
          {/* TROIS absences distinctes, longtemps dites d'une seule façon :
              « Aucun élève dans cette classe » s'affichait aussi à un enseignant
              qui n'a AUCUNE classe — le message nommait alors une cause fausse,
              et le laissait chercher des élèves dans une classe inexistante. */}
          {etatClasses === 'chargement' ? (
            <p role="status" className="m-0 text-sm text-muted-foreground">
              Chargement de vos classes…
            </p>
          ) : etatClasses === 'erreur' ? (
            <div role="alert" className="flex flex-col items-start gap-2">
              <p className="m-0 text-sm text-muted-foreground">
                Vos classes n&apos;ont pas pu être chargées.
              </p>
              <button
                type="button"
                onClick={() => setTentativeClasses((n) => n + 1)}
                className="min-h-[44px] px-4 rounded-sm bg-naia-blue text-card font-bold text-[13px] border-none cursor-pointer"
              >
                Réessayer
              </button>
            </div>
          ) : classes.length === 0 ? (
            <p className="m-0 text-sm text-muted-foreground">
              Aucune classe ne vous est rattachée. Votre direction peut vous en affecter une ;
              cet écran s&apos;ouvrira dès qu&apos;elle l&apos;aura fait.
            </p>
          ) : (
            <>
            {etatEleves === 'chargement' ? (
            <p role="status" className="m-0 mb-3 text-sm text-muted-foreground">
              Chargement des élèves…
            </p>
          ) : etatEleves === 'erreur' ? (
            <div role="alert" className="mb-3 flex flex-col items-start gap-2">
              <p className="m-0 text-sm text-muted-foreground">
                Impossible de charger les élèves de cette classe.
              </p>
              <button
                type="button"
                onClick={() => setTentativeEleves((n) => n + 1)}
                className="min-h-[44px] px-4 rounded-sm bg-naia-blue text-card font-bold text-[13px] border-none cursor-pointer"
              >
                Réessayer
              </button>
            </div>
          ) : eleves.length === 0 ? (
            <p className="m-0 mb-3 text-sm text-muted-foreground">
              Aucun élève inscrit dans cette classe pour le moment.
            </p>
          ) : (
            <ul className="list-none m-0 p-0 flex flex-col gap-2">
              {eleves.map((el) => {
                const dernier = progression.find((p) => p.studentId === el.id);
                /* DEUX lectures de suite sous le repère. Le tableau affichait des
                   positions et n'alertait jamais : une position se consulte quand
                   on y pense, une alerte se voit quand on ne la cherche pas.
                   L'API rend le plus récent d'abord. */
                const decrochage = enDecrochage(progression.filter((p) => p.studentId === el.id));
                return (
                  <li key={el.id} className="flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-2">
                    <a
                      href={avecContexteNav(`/mini-apps/fluence-eleve-detail?student=${el.id}&niveauClasse=${encodeURIComponent(classe?.level_code ?? "")}`, {
                        contexte: classe?.name ?? '',
                        retourHref: `/mini-apps/fluence-enseignant?classe=${classeId}`,
                        retourLabel: 'Suivi de classe',
                      })}
                      aria-label={`Progression de ${el.first_name} ${el.last_name}`}
                      className="min-w-0 flex-1 min-h-[44px] flex items-center gap-3 no-underline"
                    >
                      {/* Souligné au survol : sans indice visuel, un nom en gras
                          ne se distingue pas d'un simple libellé. */}
                      <span className="font-bold text-sm text-foreground truncate underline decoration-transparent hover:decoration-current underline-offset-2">
                        {el.first_name} {el.last_name}
                      </span>
                      {decrochage ? (
                        <span
                          title="Deux lectures de suite sous le repère de son niveau"
                          className="shrink-0 rounded-full bg-naia-orange-bg px-[9px] py-[3px] text-[11px] font-extrabold uppercase tracking-[0.05em] text-naia-orange"
                        >
                          à suivre
                        </span>
                      ) : null}
                      {dernier ? (
                        <span className={`ml-auto shrink-0 text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full ${pastilleDe(dernier.position).cls}`}>
                          {dernier.mclm} MCLM{estExtrapolee(dernier.dureeSecondes) ? ' · extrapolé' : ''} · {pastilleDe(dernier.position).label}
                        </span>
                      ) : (
                        <span className="ml-auto shrink-0 text-[13px] text-muted-foreground">pas encore évalué</span>
                      )}
                    </a>
                    <button
                      type="button"
                      onClick={() => void evaluer(el)}
                      disabled={enCours || !supportId}
                      className="shrink-0 min-h-[44px] px-4 rounded-sm bg-naia-blue text-card font-bold text-[13px] border-none cursor-pointer disabled:opacity-60"
                    >
                      Évaluer
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

            {/* L'exception, et elle se présente comme telle : repliée par
                défaut, et le texte dit d'où vient la voie normale. Un bouton
                aussi visible que « Évaluer » inviterait à inscrire la classe
                ici, ce qui n'est pas son objet. */}
            {etatEleves !== 'charge' ? null : ajoutOuvert ? (
              <div className="mt-3 flex flex-col gap-2 rounded-sm border border-border bg-card p-3">
                <div className="flex flex-wrap gap-2">
                  <input
                    value={prenom}
                    onChange={(e) => setPrenom(e.target.value)}
                    placeholder="Prénom"
                    aria-label="Prénom de l’élève"
                    className="h-11 min-h-[44px] flex-1 min-w-0 rounded-sm border border-border bg-card px-3 text-sm text-foreground"
                  />
                  <input
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                    placeholder="Nom"
                    aria-label="Nom de l’élève"
                    className="h-11 min-h-[44px] flex-1 min-w-0 rounded-sm border border-border bg-card px-3 text-sm text-foreground"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void ajouterEleve()}
                    disabled={enCours}
                    className="min-h-[44px] px-4 rounded-sm bg-naia-green text-card font-bold text-[13px] border-none cursor-pointer disabled:opacity-60"
                  >
                    {enCours ? 'Ajout…' : 'Ajouter à la classe'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAjoutOuvert(false); setPrenom(''); setNom(''); }}
                    disabled={enCours}
                    className="min-h-[44px] px-4 rounded-sm bg-card text-foreground border border-border font-bold text-[13px] cursor-pointer disabled:opacity-60"
                  >
                    Annuler
                  </button>
                </div>
                <p className="m-0 text-[13px] text-muted-foreground">
                  Pour inscrire toute une classe, c’est la direction qui s’en charge. Ce bouton
                  dépanne pour un enfant qui manque de la liste.
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAjoutOuvert(true)}
                className="mt-3 min-h-[44px] px-4 rounded-sm bg-card text-foreground border border-border font-bold text-[13px] cursor-pointer"
              >
                Ajouter un élève manquant
              </button>
            )}
            </>
          )}
        </section>

      </main>
    </>
  );
}
